// ABOUTME: hook for managing tenant branding data - fetches and caches branding assets
// ABOUTME: provides default fallbacks for logo and background when tenant branding not available

import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { TenantInfo, useGetTenantBySlugQuery } from "../services/tenantSlice";
import {
  getCachedTenantData,
  getMostRecentTenantData,
} from "../utils/tenantBrandingCache";

interface UseTenantBrandingResult {
  tenantInfo: TenantInfo | null;
  isLoading: boolean;
  error: string | null;
  logo?: string;
  backgroundImage?: string;
  primaryColor?: string;
  tenantName: string | null;
}

export const useTenantBranding = (): UseTenantBrandingResult => {
  const [searchParams] = useSearchParams();
  const { tenantSlug: urlTenantSlug } = useParams();
  const [fallbackTenantInfo, setFallbackTenantInfo] =
    useState<TenantInfo | null>(null);

  // Prioritize tenant slug from URL path, fall back to query parameter for backward compatibility
  const tenantSlug = urlTenantSlug || searchParams.get("tenant");

  // Skip RTK Query if no tenant slug (we'll use cached data instead)
  const {
    data: tenantData,
    isLoading: queryLoading,
    error: queryError,
  } = useGetTenantBySlugQuery(tenantSlug || "", {
    skip: !tenantSlug,
  });

  useEffect(() => {
    if (!tenantSlug) {
      // no tenant specified, try to use most recently cached tenant
      const mostRecentCached = getMostRecentTenantData();
      if (mostRecentCached) {
        setFallbackTenantInfo({
          _id: mostRecentCached.slug, // fallback ID
          name: mostRecentCached.name || mostRecentCached.slug,
          slug: mostRecentCached.slug,
          branding: mostRecentCached.branding,
        });
      } else {
        setFallbackTenantInfo(null);
      }
      return;
    }

    // try cached data first for immediate theming
    const cachedData = getCachedTenantData(tenantSlug);
    if (cachedData && !tenantData) {
      setFallbackTenantInfo({
        _id: tenantSlug, // fallback ID
        name: cachedData.name || tenantSlug,
        slug: tenantSlug,
        branding: cachedData.branding,
      });
    }
  }, [tenantSlug, tenantData]);

  // Use RTK Query data if available, otherwise fallback to cached data
  const tenantInfo = tenantData || fallbackTenantInfo;
  const isLoading = tenantSlug ? queryLoading : false;
  const error = queryError
    ? "data" in queryError &&
      typeof queryError.data === "object" &&
      queryError.data &&
      "message" in queryError.data
      ? String(queryError.data.message)
      : "Failed to fetch tenant info"
    : null;

  // compute derived values
  const logo = tenantInfo?.branding?.logo || undefined;
  const backgroundImage = tenantInfo?.branding?.backgroundImage;
  const primaryColor = tenantInfo?.branding?.primaryColor;
  const tenantName = tenantInfo?.name || null;

  return {
    tenantInfo,
    isLoading,
    error,
    logo,
    backgroundImage,
    primaryColor,
    tenantName,
  };
};
